import {LocalStorage} from "../../common/clientStorage";
import {autoRegister, resolve} from "../../container";
import type {Maybe} from "../../bootstrap/common/types";
import type {PropertyMap} from "../../common/utils/objects";
import {isDefined} from "../../common/utils/basics";

const PAGES_WITH_SUBMITTED_FEEDBACK_KEY = "feedback";

@autoRegister()
export class FeedbackStorage {

    public constructor(private localStorage: LocalStorage = resolve(LocalStorage)) {
    }

    public saveQualifiedFeedbackFor(pageUrl: string): void {
        const feedback = this.submittedFeedback(pageUrl);
        if (!feedback) {
            throw new Error(`No feedback found for page ${pageUrl} to mark as qualified feedback submitted.`);
        }
        feedback.qualifiedFeedbackSubmitted = true;
        this.saveFeedback(pageUrl, feedback);
    }

    public isQualifiedFeedbackGivenFor(pageUrl: string): boolean {
        const feedback = this.submittedFeedback(pageUrl);
        return feedback?.qualifiedFeedbackSubmitted ?? false;
    }

    public isQualifiedFeedbackGivenForChoice(choice: string): boolean {
        const submittedFeedbacks = this.submittedFeedbacks();
        if (!submittedFeedbacks) {
            return false;
        }
        return Object.keys(submittedFeedbacks)
            .map(key => submittedFeedbacks[key])
            .filter(isDefined)
            .some(feedback => feedback.choice === choice && feedback.qualifiedFeedbackSubmitted);
    }

    private submittedFeedbacks(): Maybe<PropertyMap<Feedback>> {
        return this.localStorage.fetch(PAGES_WITH_SUBMITTED_FEEDBACK_KEY);
    }

    private submittedFeedback(pagePath: string): Maybe<Feedback> {
        return this.localStorage.fetchFromPath([PAGES_WITH_SUBMITTED_FEEDBACK_KEY, pagePath]) as Maybe<Feedback>;
    }

    public saveFeedbackId(pageUrl: string, choice: string, id: string): void {
        const feedback: Feedback = {id: id, choice: choice, qualifiedFeedbackSubmitted: false};
        this.saveFeedback(pageUrl, feedback);
    }

    private saveFeedback(pageUrl: string, feedback: Feedback): void {
        this.localStorage.saveInPath(PAGES_WITH_SUBMITTED_FEEDBACK_KEY, [pageUrl], feedback);
    }

    public getFeedbackIdFor(pageUrl: string): Maybe<string> {
        const feedback = this.submittedFeedback(pageUrl);
        return feedback?.id;
    }

    public getFeedbackChoiceFor(pageUrl: string): Maybe<string> {
        const feedback = this.submittedFeedback(pageUrl);
        return feedback?.choice;
    }
}

type Feedback = {
    id: string;
    choice: string;
    qualifiedFeedbackSubmitted: boolean;
}